import { useEffect, useState } from "react";
import DashboardLayout, { StatCard, EmptyState } from "./DashboardLayout";
import { LayoutDashboard, Users, Brain, TrendingUp, Layers, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api from "@/lib/api";

const NAV = [
    { key: "overview", label: "Corporate Dashboard", icon: LayoutDashboard },
    { key: "capability", label: "Capability Levels", icon: Layers },
    { key: "employees", label: "Employees", icon: Users },
];

const LEVEL_LABEL = {
    C0: "Digital Awareness", C1: "Digital Foundation", C2: "Digital Productivity", C3: "AI Ready",
    C4: "AI Practitioner", C5: "AI Workflow Practitioner", C6: "AI Automation Specialist",
    C7: "AI Leader", C8: "AI Transformation Leader",
};

function Bar({ label, value, color = "primary" }) {
    return (
        <div>
            <div className="flex justify-between text-xs text-foreground/60 mb-1"><span>{label}</span><span className="font-medium">{value}%</span></div>
            <div className="h-2 rounded-full bg-muted overflow-hidden"><div className={`h-full rounded-full bg-${color}`} style={{ width: `${value}%` }} /></div>
        </div>
    );
}

export default function CorporateDashboard() {
    const [tab, setTab] = useState("overview");
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/corporate/dashboard").then((r) => setData(r.data)).catch(() => setData({})); }, []);
    if (!data) return <DashboardLayout nav={NAV} active={tab} onNav={setTab} title="Corporate Academy"><div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></DashboardLayout>;

    const s = data.stats || {};
    const dist = s.distribution || {};
    const maxDist = Math.max(...Object.values(dist), 1);
    return (
        <DashboardLayout nav={NAV} active={tab} onNav={setTab} title={data.corporate ? data.corporate.name + " — AI Academy" : "Corporate Academy"}>
            {!data.corporate ? <EmptyState icon={Users} title="No organization linked" desc="Your corporate account is being set up." /> : tab === "overview" ? (
                <div className="space-y-6">
                    <div className="rounded-2xl bg-secondary text-secondary-foreground p-6 sm:p-8 flex flex-wrap items-center justify-between gap-4">
                        <div>
                            <p className="text-xs uppercase tracking-wide text-secondary-foreground/50">LARUTA Organizational AI Readiness Score</p>
                            <p className="mt-1 font-heading text-5xl font-extrabold">{s.lairs}<span className="text-2xl text-secondary-foreground/40">/100</span></p>
                        </div>
                        <Badge className="rounded-full bg-accent/20 text-accent border-0 text-sm px-4 py-1.5" data-testid="maturity-badge">Maturity: {s.maturity}</Badge>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                        <StatCard icon={Users} label="Employees" value={s.employees} />
                        <StatCard icon={TrendingUp} label="Avg Progress" value={`${s.avg_progress}%`} color="success" />
                        <StatCard icon={Brain} label="AI Readiness (LAIRS)" value={`${s.lairs}%`} color="accent" />
                    </div>
                    <Card className="p-6">
                        <h3 className="font-heading font-bold mb-5">AI readiness dimensions</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                            {Object.entries(s.dimensions || {}).map(([k, v], i) => <Bar key={k} label={k} value={v} color={i % 3 === 0 ? "primary" : i % 3 === 1 ? "accent" : "success"} />)}
                        </div>
                    </Card>
                </div>
            ) : tab === "capability" ? (
                <Card className="p-6">
                    <h3 className="font-heading font-bold mb-5">Workforce capability distribution (C0–C8)</h3>
                    <div className="space-y-3">
                        {Object.entries(dist).map(([lv, v]) => (
                            <div key={lv} className="flex items-center gap-3" data-testid={`level-${lv}`}>
                                <span className="w-10 font-mono text-sm font-semibold">{lv}</span>
                                <span className="w-52 text-sm text-foreground/60 hidden sm:block">{LEVEL_LABEL[lv]}</span>
                                <div className="flex-1 h-6 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-primary" style={{ width: `${(v / maxDist) * 100}%` }} /></div>
                                <span className="w-6 text-sm font-medium text-right">{v}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            ) : (
                <Card className="p-0 overflow-hidden overflow-x-auto">
                    <Table>
                        <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Department</TableHead><TableHead>Capability</TableHead><TableHead>Progress</TableHead></TableRow></TableHeader>
                        <TableBody>
                            {(data.employees || []).map((e) => (
                                <TableRow key={e.id} data-testid={`employee-${e.id}`}>
                                    <TableCell className="font-medium">{e.name}</TableCell>
                                    <TableCell className="text-foreground/60">{e.dept}</TableCell>
                                    <TableCell><Badge variant="secondary" className="rounded-full">{e.capability_level} · {LEVEL_LABEL[e.capability_level]}</Badge></TableCell>
                                    <TableCell>{e.progress}%</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            )}
        </DashboardLayout>
    );
}
